import type { ReactNode } from 'react'
import type { ProfileViewProps } from '../profile/profile-editable-view.js'
import { ProfileView } from '../profile/profile-editable-view.js'

export type PublicProfileViewProps = ProfileViewProps & {
  /** The share link's id, added to links into SeedForge so a sign-up can be traced back to it. */
  shareLinkId?: string
  /** Beside the logo, for a signed-in investor: the Follow control. */
  headerActions?: ReactNode
}

/**
 * The marketing site, which is a different app from this one.
 *
 * Hardcoded to match the convention already in the codebase (the outreach help
 * links do the same) rather than threaded through an env var this package has
 * no access to.
 */
const SEEDFORGE_URL = 'https://seedforge.com'

/**
 * The investor page, tagged with where the visitor came from. The website
 * carries `ref` (and `share_link`) on to sign-up, where the backend stores it
 * as the account's sign-up source (AD-030). The id opens nothing; the private
 * token in the /s/ address never leaves the page.
 */
export function investorPageUrl(shareLinkId?: string): string {
  const params = new URLSearchParams({ ref: 'seedforge_profile' })
  if (shareLinkId) params.set('share_link', shareLinkId)
  return `${SEEDFORGE_URL}/investors?${params}`
}

/**
 * Investor-facing public profile — wraps ProfileView in a full-page layout
 * with branded background gradient and logo.
 */
export function PublicProfileView({
  shareLinkId,
  headerActions,
  ...props
}: PublicProfileViewProps) {
  return (
    <div className="relative min-h-screen bg-[#111111] pb-12 text-white">
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#f97316]/10 via-background to-background" />
      <div className="relative flex items-center justify-between gap-4 px-6 pt-6 lg:px-12 lg:pt-12">
        {/* The reader here is an investor, so the logo goes to the investor
            page rather than the founder-facing home. It opens in a new tab on
            purpose: this surface is a deck someone is part-way through, and a
            same-tab navigation costs them their place — and the founder the
            visit. */}
        <a
          href={investorPageUrl(shareLinkId)}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="SeedForge for investors"
          className="inline-block rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <img
            src="/seedforge-logo.svg"
            alt="SeedForge"
            className="h-8 transition-opacity hover:opacity-80"
          />
        </a>
        {headerActions && <div className="flex items-center gap-2">{headerActions}</div>}
      </div>
      <main className="relative mx-auto max-w-5xl px-4 py-6 lg:px-0 lg:py-8">
        <ProfileView {...props} isPublic />
      </main>
    </div>
  )
}
